import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { PictureOutlined } from '@ant-design/icons';
import { Image, List, Spin } from 'antd';
import VirtualList from 'rc-virtual-list';
import { beautifyThePrice, replaceEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import V2Empty from '@/common/components/Data/V2Empty';
import IconFont from '@/common/components/IconFont';
import V2Pagination from '@/common/components/Data/V2Pagination';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import { postTrustSpotLibraryPage } from '@/common/api/locxxTrust';
import { UrlSuffix } from '../../../enums/qiniu';
import { getPointAreaAndHeightLines, mapTrustPointListResponse, normalizePointSearchValues, normalizePositioningBrand } from './helpers';
import styles from './index.module.less';

interface PointListProps {
  filters?: any;
  mainHeight?: number;
  initialPageSize?: number;
  hidePagination?: boolean;
  fitContentHeight?: boolean;
  displayMode?: 'default' | 'areaDetail';
  allowProjectJump?: boolean;
  onViewRecord?: (record: any) => void;
  onOpenProject?: (record: any) => void;
  onOpenVideoDetail?: (record: any) => void;
  onOpenRoi?: (record: any) => void;
  onDataLoaded?: (data: { total: number; list: any[] }) => void;
}

interface PointListRow {
  key: string;
  records: any[];
}

interface PointCardProps {
  record: any;
  onViewRecord?: (record: any) => void;
  onOpenProject?: (record: any) => void;
  onOpenVideoDetail?: (record: any) => void;
  onOpenRoi?: (record: any) => void;
  allowProjectJump?: boolean;
  displayMode?: 'default' | 'areaDetail';
}

const CARD_ROW_HEIGHT = 202;
const flowSourceTagTextMap: Record<string, string> = {
  FLOW: '实时客流',
  CHECK_SPOT: '踩点客流',
  BOTH: '实时客流',
};

const PointCard: FC<PointCardProps> = ({ record, onViewRecord, onOpenProject, onOpenVideoDetail, onOpenRoi, allowProjectJump = true, displayMode = 'default' }) => {
  const [imageFailed, setImageFailed] = useState<boolean>(false);
  const methods = useMethods({
    handleViewRecord() {
      onViewRecord?.(record);
    },
    handleOpenProject() {
      if (record?.modelClusterId) {
        onOpenProject?.(record);
      }
    },
    handleOpenVideoDetail() {
      onOpenVideoDetail?.(record);
    },
    handleOpenVideoDetailKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        methods.handleOpenVideoDetail();
      }
    },
    handleOpenRoi() {
      onOpenRoi?.(record);
    },
    handleCoverImageError() {
      setImageFailed(true);
    },
  });
  const hasVideoDetail = record?.videoSource === 'FLOW' || record?.videoSource === 'CHECK_SPOT' || record?.videoSource === 'BOTH';
  const canViewRecord = Boolean(onViewRecord);
  const canOpenVideoDetail = Boolean(hasVideoDetail && onOpenVideoDetail);
  const pointName = record?.spotName || '';
  const placeName = record?.placeName || '';
  const canOpenProject = Boolean(allowProjectJump && record?.modelClusterId && onOpenProject);
  const pointTitle = [placeName, pointName].filter(Boolean).join(' - ');
  const coverImageUrl = record?.coverUrl?.includes('img.linhuiba.com/')
    ? `${record.coverUrl}${UrlSuffix.Ori}`
    : record?.coverUrl?.includes('pmsimage.location.pub/')
      ? `${record.coverUrl}${UrlSuffix.PmsLarge}`
      : record?.coverUrl;
  const positioningBrand = normalizePositioningBrand(record?.positioningbrand);
  const flowSourceTagText = flowSourceTagTextMap[record?.videoSource];
  const flowText = record?.measuredDailyFlow || record?.measuredDailyFlow === 0
    ? beautifyThePrice(record.measuredDailyFlow, ',', 'auto')
    : '--';
  const priceText = record?.dailyPublishPrice || record?.dailyPublishPrice === 0
    ? `¥${beautifyThePrice(record.dailyPublishPrice, ',', 'auto')}`
    : '--';
  const cpmText = record?.cpm || record?.cpm === 0
    ? `CPM：¥${beautifyThePrice(record.cpm, ',', 'auto')}`
    : 'CPM：--';
  const areaAndHeight = getPointAreaAndHeightLines(record);
  const areaText = record?.spotArea || record?.spotArea === 0 ? `面积：${beautifyThePrice(record.spotArea, ',', 'auto')}㎡` : '';
  const heightText = areaAndHeight.secondary
    ? areaAndHeight.secondary.replace('限高 ', '限高：')
    : !areaText && areaAndHeight.primary !== '--' ? `限高：${areaAndHeight.primary}` : '';
  const specification = [
    areaText,
    heightText,
    record?.spotFloor ? `楼层：${record.spotFloor}` : '',
  ].filter(Boolean).join(' ｜ ');
  const flowInfo = `日均客流：${flowText}`;
  const surroundBrands = positioningBrand.length ? `周边品牌：${positioningBrand.join('、')}` : '';

  return (
    <div className={`${styles.pointCard} ${displayMode === 'areaDetail' ? styles.pointCardAreaDetail : ''}`}>
      <div className={styles.imagePlaceholder} aria-label='点位图片占位'>
        {record?.coverUrl && !imageFailed
          ? <Image
            alt=''
            className={styles.coverImage}
            height='100%'
            preview
            src={coverImageUrl}
            width='100%'
            wrapperClassName={styles.coverImageWrapper}
            onError={methods.handleCoverImageError}
          />
          : <PictureOutlined />}
      </div>
      <div className={styles.cardContent}>
        <div className={styles.titleRow}>
          <div className={styles.titleText}>
            {pointTitle
              ? <V2DetailItem
                className={`${styles.pointName} ${canViewRecord ? '' : styles.pointNameDisabled}`}
                moduleType='easy'
                noStyle
                value={pointTitle}
                onClick={canViewRecord ? methods.handleViewRecord : undefined}
              />
              : <span>--</span>}
          </div>
          <div className={styles.tags}>
            {flowSourceTagText ? <span className={styles.tag}>{flowSourceTagText}</span> : null}
            {record?.spotPosition ? <span className={styles.tag}>{record.spotPosition}</span> : null}
            {record?.indoorOrOutdoor ? <span className={styles.tag}>{record.indoorOrOutdoor}</span> : null}
          </div>
        </div>
        <div className={styles.infoLine}>
          <IconFont iconHref='asset-pc-ic_shuju' className={styles.infoIcon}/>
          <span
            className={canOpenVideoDetail ? styles.flowValueClickable : undefined}
            role={canOpenVideoDetail ? 'button' : undefined}
            tabIndex={canOpenVideoDetail ? 0 : undefined}
            onClick={canOpenVideoDetail ? methods.handleOpenVideoDetail : undefined}
            onKeyDown={canOpenVideoDetail ? methods.handleOpenVideoDetailKeyDown : undefined}
          >
            <V2DetailItem
              className={styles.detailText}
              moduleType='easy'
              noStyle
              value={flowInfo}
            />
          </span>
          {specification ? <span className={styles.specification}> ｜ {specification}</span> : null}
        </div>
        {surroundBrands ? <div className={styles.surroundBrands}><IconFont iconHref='asset-pc-ic_pinpai' className={styles.infoIcon}/><V2DetailItem className={styles.detailText} moduleType='easy' noStyle value={surroundBrands}/></div> : null}
        <button type='button' className={styles.projectName} disabled={!canOpenProject} onClick={canOpenProject ? methods.handleOpenProject : undefined}>
          <IconFont iconHref='asset-pc-ic_shangchang' className={styles.infoIcon}/>
          {displayMode === 'areaDetail'
            ? <V2DetailItem className={styles.projectNameText} moduleType='easy' noStyle value={replaceEmpty(record?.placeName)}/>
            : <span>{replaceEmpty(record?.placeName)}</span>}
        </button>
        <div className={styles.priceRow}>
          <span className={styles.priceText}>日刊例价：<span className={styles.priceValue}>{priceText}</span></span>
          <span className={styles.cpmText}>（{cpmText}）</span>
        </div>
        <button type='button' className={`${styles.roiButton} ${displayMode === 'areaDetail' ? styles.roiButtonAreaDetail : ''}`} disabled={!onOpenRoi} onClick={onOpenRoi ? methods.handleOpenRoi : undefined}>预估ROI</button>
      </div>
    </div>
  );
};

const PointList: FC<PointListProps> = ({
  filters,
  mainHeight = 0,
  initialPageSize = 20,
  hidePagination = false,
  fitContentHeight = false,
  displayMode = 'default',
  allowProjectJump = true,
  onViewRecord,
  onOpenProject,
  onOpenVideoDetail,
  onOpenRoi,
  onDataLoaded,
}) => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const requestSequenceRef = useRef<number>(0);
  const previousFiltersRef = useRef<any>();
  const listHeight = Math.max(mainHeight - 64, 260);
  const rows = useMemo<PointListRow[]>(() => {
    const nextRows: PointListRow[] = [];
    for (let index = 0; index < list.length; index += 2) {
      const records = list.slice(index, index + 2);
      nextRows.push({
        key: records.map((record, recordIndex) => record?.spotId || record?.tenantSpotId || record?.id || `${index}-${recordIndex}`).join('-'),
        records,
      });
    }
    return nextRows;
  }, [list]);
  const contentHeight = rows.length ? rows.length * CARD_ROW_HEIGHT : CARD_ROW_HEIGHT;
  const virtualListHeight = fitContentHeight ? Math.min(listHeight, contentHeight) : listHeight;

  const methods = useMethods({
    loadData() {
      const sequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = sequence;
      setLoading(true);
      postTrustSpotLibraryPage({
        page,
        size: pageSize,
        ...normalizePointSearchValues(filters || {}),
      }).then((response) => {
        if (sequence !== requestSequenceRef.current) {
          return;
        }
        const result = mapTrustPointListResponse(response);
        setList(result.list);
        setTotal(result.total);
        onDataLoaded?.(result);
      }, () => {
        if (sequence !== requestSequenceRef.current) {
          return;
        }
        setList([]);
        setTotal(0);
        onDataLoaded?.({ total: 0, list: [] });
      }).finally(() => {
        if (sequence === requestSequenceRef.current) {
          setLoading(false);
        }
      });
    },
    handlePageChange(nextPage: number, nextPageSize: number) {
      if (nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
        setPage(1);
        return;
      }
      setPage(nextPage);
    },
    renderRow(row: PointListRow) {
      return (
        <List.Item key={row.key} className={styles.virtualItem}>
          <div className={styles.cardRow}>
            {row.records.map((record) => (
              <div className={styles.cardSlot} key={record?.spotId || record?.tenantSpotId || record?.id}>
                <PointCard
                  record={record}
                  onViewRecord={onViewRecord}
                  onOpenProject={onOpenProject}
                  onOpenVideoDetail={onOpenVideoDetail}
                  onOpenRoi={onOpenRoi}
                  allowProjectJump={allowProjectJump}
                  displayMode={displayMode}
                />
              </div>
            ))}
          </div>
        </List.Item>
      );
    },
  });

  useEffect(() => {
    if (previousFiltersRef.current !== filters) {
      previousFiltersRef.current = filters;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    methods.loadData();
  }, [filters, page, pageSize]);

  return (
    <div className={styles.pointList}>
      <Spin spinning={loading}>
        {rows.length
          ? <List split={false} className={styles.list}>
            <VirtualList
              data={rows}
              height={virtualListHeight}
              itemHeight={CARD_ROW_HEIGHT}
              itemKey='key'
            >
              {methods.renderRow}
            </VirtualList>
          </List>
          : !loading ? <V2Empty type='nothing' centerInBlock className={styles.empty}/> : <div className={styles.loadingPlaceholder}/>}
      </Spin>
      {!hidePagination ? <V2Pagination
        className={styles.pagination}
        current={page}
        pageSize={pageSize}
        total={total}
        pageSizeOptions={[20, 50, 100]}
        onChange={methods.handlePageChange}
      /> : null}
    </div>
  );
};

export default PointList;
